import { SolutionPage } from "@/components/shared/SolutionPage";
import { WebsiteBuildVisual } from "@/components/shared/WebsiteBuildVisual";
import { Zap, MousePointerClick, Smartphone, Rocket, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SystemLadder } from "@/components/shared/SystemLadder";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

const Websites = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Website Design"
      seo={{
        title: "Website Design for Lead-Driven Businesses | Built to Convert | Frayze",
        description: "High-speed, conversion-focused websites built to capture and qualify leads. Mobile-first design, built-in lead forms, and fast hosting: go live in 7 days.",
        keywords: "website design, conversion-focused websites, high-speed web design, lead capture websites, custom web design, web design for service businesses",
        canonical: "https://frayze.ca/solutions/websites/"
      }}
      hero={{
        badge: "Lead-Driven Web Design",
        badgeColor: "text-blue-600",
        title: "A website that converts browsers into leads.",
        description: "Most websites look good but don't work. We build for conversion: fast pages, clear offers, and built-in lead capture. Go live in 7 days.",
        buttonColor: "bg-blue-600 hover:bg-blue-700",
        gradientParams: "from-blue-500/5 via-background to-background",
        primaryCta: { text: "See Our Process", link: "#how-it-works" },
        secondaryCta: { text: "Book a Free 15-Min Call", link: "/book-consultation/" },
        visual: <WebsiteBuildVisual />
      }}
      localStory={{
        eyebrow: "The conversion gap",
        client: "What you're missing",
        location: "Most websites",
        accentColor: "text-blue-600",
        story: (
          <>
            A business owner drives traffic to their site. Visitors land, scroll, and leave. No quote. No call. No lead. The site looks professional, but it doesn't have a job, and that costs tens of thousands of dollars in lost revenue.
          </>
        ),
        result: "A conversion-focused website turns traffic into pipeline."
      }}
      problem={{
        title: "Most Websites Fail at Their One Job: Turning Visitors Into Leads",
        description: (
          <>
            A site can rank well, look beautiful, and still fail because it doesn&rsquo;t convert. Slow load times, unclear offers, broken forms, and no clear path to action kill conversion rates before they start.
            <br /><br />
            <strong>The gap between traffic and leads is the gap between a cost center and a revenue engine.</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "3s", label: "Load time before half of mobile visitors leave your site." },
          { value: "73%", label: "Of users will leave if a site takes more than 3 seconds to load." },
          { value: "50%", label: "Of conversions lost due to unclear offers or broken forms." }
        ]
      }}

      systemModel={{
        title: "How a Conversion-Focused Site Gets Built",
        description: "From strategy to live: a process built to turn browsers into leads.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-blue-200 via-blue-400 to-teal-400",
        items: [
          { step: "1", title: "Architect", description: "Map your offer, audience, and the path to action", icon: Zap },
          { step: "2", title: "Build", description: "High-speed pages with built-in lead forms and clear CTAs", icon: Smartphone },
          { step: "3", title: "Convert", description: "Test headlines, offers, and form fields to maximize results", icon: MousePointerClick },
          { step: "4", title: "Launch", description: "Go live in 7 days. Leads start flowing immediately", icon: Rocket }
        ]
      }}

      layers={{
        title: "What's Actually in the Website",
        description: "Speed, clarity, and conversion. That's the whole build.",
        theme: "blue",
        items: [
          {
            title: "5-Page Conversion Structure",
            icon: Smartphone,
            items: [
              "Home: Your clearest offer",
              "Services: What you do & why it matters",
              "Proof: Real client results & case studies",
              "Process: How you work (remove friction)",
              "Lead Capture: Forms that work & CTAs that convert"
            ]
          },
          {
            title: "Mobile-First & Fast",
            icon: Zap,
            items: [
              "Built for mobile (80% of traffic)",
              "Sub-2 second load times",
              "Optimized images & lazy loading",
              "Fast cloud hosting (CDN)",
              "Accessibility-checked builds (WCAG 2.1 AA targets, AODA-aware)"
            ]
          },
          {
            title: "SEO Foundation + Local Schema",
            icon: Smartphone,
            items: [
              "On-page SEO (titles, meta, structure)",
              "Local schema markup (maps, phone, hours)",
              "Open Graph for social sharing",
              "Mobile-friendly indexing"
            ]
          }
        ]
      }}

      interdependence={{
        title: "A fast website is the foundation. Local SEO gets it found.",
        description: (
          <>
            Building a conversion-focused site is half the job. Getting it found is the other half. Every site we build includes on-page SEO, but to rank and get found by local customers, pair this with <Link to="/solutions/local-seo-reputation/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">Local SEO & Reputation Management</Link>.
            <br /><br />
            Then connect it to your <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM system</Link> so every lead gets answered in under 5 minutes.
            <br /><br />
            For paid traffic on top of organic search, see the <Link to="/solutions/lead-generation-system/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">Lead Generation System</Link>.
            <br /><br />
            Still choosing a platform? Our guide to the <Link to="/blog/best-website-platform-canadian-businesses-2026/" className="font-bold underline decoration-blue-500/50 hover:text-blue-600 transition-colors">best website platform for Canadian businesses</Link> compares the options side by side.
          </>
        ),
        icon: Zap,
        quote: "A fast, clear website turns traffic into leads. Local SEO turns browsers into customers."
      }}

      useCases={{
        title: "Built for Every Industry That Sells Locally",
        items: [
          { title: "Trades & Contractors", description: "Home pages that rank, forms that convert, jobs that get booked.", link: "/industries/trades/" },
          { title: "Professional Services", description: "Authority sites that establish trust and capture qualified leads.", link: "/solutions/" },
          { title: "Local Retail & Hospitality", description: "Mobile-first sites that drive foot traffic and reservations.", link: "/solutions/" }
        ]
      }}

      socialProof={{
        title: "Built for conversion",
        quote: "One system: a fast site with clear offers, built-in lead forms, and local schema, measured by the leads it captures, not just the traffic.",
        icon: MousePointerClick,
        ctaLink: "/case-studies/",
        ctaText: "See what conversion looks like"
      }}

      cta={{
        title: "Build your conversion engine.",
        description: "Let's map your site, build it fast, and turn traffic into pipeline.",
        primaryCta: { text: "Start Your Free Website Audit", link: "/digital-audit/" },
        secondaryCta: { text: "Book a Free 15-Min Call", link: "/book-consultation/" },
        color: "bg-blue-600 hover:bg-blue-700"
      }}

      customSection={
        <div className="space-y-24 pb-24">
          {/* WEBSITE LAUNCH PROGRAM */}
          <section className="container mx-auto px-6 max-w-4xl text-center">
            <div className="reveal lift-hover bg-primary/5 border border-primary/20 rounded-xl p-8">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Rocket className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold">Need a Website in 7 Days?</h3>
              </div>
              <p className="text-muted-foreground mb-6">
                Our <strong>Website Launch Program</strong> gets you live fast: a 5-page site, mobile-optimized, with lead forms built in.
              </p>
              <Button variant="outline" asChild>
                <Link to="/website-launch-program">
                  View Launch Program <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </section>

          {/* System Ladder */}
          <section className="container mx-auto px-6 pb-12">
            <div className="reveal text-center mb-12">
              <h2 className="text-2xl font-bold mb-4">How This Fits Into Your Growth System</h2>
              <p className="text-muted-foreground">Your website is the hub. Everything else drives traffic to it.</p>
            </div>
            <div className="reveal reveal-delay-1 bg-slate-50 dark:bg-slate-900/50 border rounded-2xl p-8 overflow-hidden max-w-5xl mx-auto">
              <SystemLadder activeTier="visibility" />
            </div>
          </section>

          <ServiceFAQ
            title="Questions About Website Design"
            description="Understanding what goes into a conversion-focused site."
            items={[
              {
                question: "How long does it take to build a website?",
                answer: "Our standard build takes 4-6 weeks for full custom sites. But if you need to go live faster, our Website Launch Program gets you a 5-page lead-capture site in 7 days. It's the same conversion-focused approach, just faster."
              },
              {
                question: "Do I own my website and domain?",
                answer: "Yes. You always own your domain name (URL). If you cancel, you keep your domain and all your content. The site design itself can be exported or migrated to another platform if needed."
              },
              {
                question: "Is this WordPress?",
                answer: "We prefer our custom high-performance stack (better speed, security, and no plugin maintenance). We choose the tool that gets the best conversion result for your specific industry, but can build on WordPress if that's your requirement."
              },
              {
                question: "What if I already have a website?",
                answer: "We audit it first. If the foundation is solid but it's not converting, we optimize it. If it's built on old tech, we recommend a migration to our conversion-focused platform. Sometimes the best move is a redesign."
              },
              {
                question: "What about ongoing maintenance?",
                answer: "We handle hosting, security updates, and performance monitoring. You focus on your business. Optional: we can manage your content updates too."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default Websites;
